import AllInOneGeoAutocomplete from '@/components/autocompletes/AllInOneGeoAutocomplete'
import Vue from 'vue'
import appConfig from '@/config.js'

const ShippingsContainersEditBottomSlot = () => import('@/components/ShippingsContainersEditBottomSlot')
const shippingsContainersModel = vm => ({
  title: vm.$gettext('Shippings Containers'),
  endpoint: 'shippings-containers',
  allowNew: true,
  allowDelete: false,
  editBottomSlot: ShippingsContainersEditBottomSlot,
  fields: [
    {
      key: 'id',
      sortable: true
    },
    {
      key: 'from_place_id',
      label: vm.$gettext('From'),
      editable: true,
      description: vm.$gettext('Origin location'),
      required: true,
      componentEdit: (value, key, item) => {
        return {
          is: AllInOneGeoAutocomplete,
          props: {
            required: true,
            staticItems: true,
            minCharacter: 4,
            preQuery: item.address_from?.formatted_address || '',
            disabled: !!item.id
          },
          on: {
            selected: async result => {
              await vm.checkPlace(result)

              if (vm.theCheckedAddress) {
                item[key] = vm.theCheckedAddress.id
              } else {
                item[key] = null
              }
            }
          }
        }
      },
      formatter: (value, key, item) => {
        return item.address_from.formatted_address
      }
    },
    {
      key: 'to_place_id',
      label: vm.$gettext('To'),
      editable: true,
      description: vm.$gettext('Target location'),
      required: true,
      componentEdit: (value, key, item) => {
        return {
          is: AllInOneGeoAutocomplete,
          props: {
            required: true,
            staticItems: true,
            minCharacter: 4,
            preQuery: item.address_to?.formatted_address || '',
            disabled: !!item.id
          },
          on: {
            selected: async result => {
              await vm.checkPlace(result)

              if (vm.theCheckedAddress) {
                item[key] = vm.theCheckedAddress.id
              } else {
                item[key] = null
              }
            }
          }
        }
      },
      formatter: (value, key, item) => {
        return item.address_to.formatted_address
      }
    },
    {
      key: 'last_known_place_id',
      label: vm.$gettext('Last Known Place'),
      editable: true,
      description: vm.$gettext('Last checkpoint location'),
      componentEdit: (value, key, item) => {
        return {
          is: AllInOneGeoAutocomplete,
          props: {
            staticItems: true,
            minCharacter: 4,
            preQuery: item.last_known_address?.formatted_address || '',
            disabled: !!item.id
          },
          on: {
            selected: async result => {
              await vm.checkPlace(result)

              if (vm.theCheckedAddress) {
                item[key] = vm.theCheckedAddress.id
              } else {
                item[key] = null
              }
            }
          }
        }
      },
      formatter: (value, key, item) => {
        return item.last_known_address.formatted_address
      }
    },
    {
      key: 'shippings',
      label: vm.$gettext('Shippings Qty.'),
      sortable: true,
      editable: false,
      hidden: true,
      formatter: (value, key, item) => {
        return value.length
      }
    },
    {
      key: 'weight',
      label: vm.$gettext('Weight'),
      editable: true,
      description: vm.$gettext('Weight in grams'),
      type: 'number',
      editCondition: item => {
        return item.status === 'DRAFT'
      }
    },
    {
      key: 'status',
      label: vm.$gettext('Status'),
      sortable: true,
      editable: true,
      required: true,
      description: vm.$gettext('Current status [ DRAFT, READY, INTRANSIT, DELIVERED, CANCELED ]'),
      type: 'select',
      options: ['DRAFT', 'READY', 'INTRANSIT', 'DELIVERED', 'CANCELED'],
      default: 'DRAFT',
      editCondition: item => {
        return false
      }
    },
    {
      key: 'pickup_shipping_id',
      label: vm.$gettext('Pickup ID'),
      sortable: false,
      editable: false,
      hidden: true,
      formatter: (value, key, item) => {
        if (value) {
          return (
            '<a href="/edit/shippings/' +
            value +
            '" target="_blank">' +
            Vue.options.filters.uuid(value) +
            '</a>'
          )
        }
      }
    },
    {
      key: 'applied_by_id',
      label: vm.$gettext('Applied By'),
      sortable: true,
      editable: false,
      hidden: true,
      formatter: (value, key, item) => {
        return '<a href="/edit/users/' + value + '" target="_blank">' + item.applied_by.name + '</a>'
      }
    },
    {
      key: 'created_at',
      label: vm.$gettext('Created At'),
      sortable: true
    },
    {
      key: 'updated_at',
      label: vm.$gettext('Updated At'),
      sortable: true
    }
  ],
  extraActions: [
    {
      label: vm.$gettext('Container Label'),
      type: 'button',
      icon: 'file-pdf',
      title: vm.$gettext('Get Container PDF label'),
      variant: 'dark',
      position: 'start',
      disabledCondition: item => {
        return item.status === 'CANCELED'
      },
      action: item => {
        const data = {
          url: appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/label',
          params: null,
          type: 'application/pdf;charset=utf-8;',
          filename: 'shippings_container_' + item.id + '_label'
        }
        Vue.http.get(data.url, { params: data.params, responseType: 'blob' }).then(response => {
          const blob = new Blob([response.body], { type: data.type })
          if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, data.filename)
          } else {
            const link = document.createElement('a')
            if (link.download !== undefined) {
              // feature detection
              // Browsers that support HTML5 download attribute
              const url = URL.createObjectURL(blob)
              link.setAttribute('href', url)
              link.setAttribute('download', data.filename)
              link.style.visibility = 'hidden'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }
          }
        })
      }
    },
    {
      label: vm.$gettext('Container Manifest'),
      type: 'button',
      icon: 'file-pdf',
      title: vm.$gettext('Get Container PDF Manifest'),
      variant: 'dark',
      position: 'start',
      disabledCondition: item => {
        return item.status === 'DRAFT' || item.status === 'CANCELED'
      },
      action: item => {
        Vue.http.get(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/manifest').then(response => {
          window.open(response.body.manifest_url)
          vm.$root.$emit('reloadItem')
        })
      }
    },
    {
      label: vm.$gettext('Set as READY'),
      type: 'button',
      icon: 'check',
      title: vm.$gettext('Set Container status as READY'),
      variant: 'success',
      position: 'start',
      disabledCondition: item => {
        return item.status !== 'DRAFT' || !item.shippings.length
      },
      action: item => {
        if (confirm(vm.$gettext('Are you sure you want to SET AS READY this Container?'))) {
          const payload = {
            status: 'READY'
          }
          Vue.http
            .patch(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/status', payload)
            .then(() => {
              vm.$root.$emit('reloadItem')
            })
        }
      }
    },
    {
      label: vm.$gettext('Set as CANCELED'),
      type: 'button',
      icon: 'trash-alt',
      title: vm.$gettext('Set Container status as CANCELED'),
      variant: 'danger',
      position: 'start',
      disabledCondition: item => {
        return item.status === 'CANCELED'
      },
      action: item => {
        if (confirm(vm.$gettext('Are you sure you want to SET AS CANCELED this Container?'))) {
          const payload = {
            status: 'CANCELED'
          }
          Vue.http
            .patch(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/status', payload)
            .then(() => {
              vm.$root.$emit('reloadItem')
            })
        }
      }
    }
  ],
  includes: ['appliedBy', 'address_from', 'address_to', 'last_known_address', 'shippings.shippingsContainer'],
  filters: ['id', 'status', 'pickup_shipping_id', 'created_at', 'updated_at', 'appliedBy.name']
})

export default shippingsContainersModel
